import { UpgradeButton } from "@components/plan-subscription-lock";
import { cn } from "@lib/cn";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Tooltip from "./tooltip";

type TabsContextType = {
  active: string | number;
  setActive: (value: string | number) => void;
  registerTab: (value: string | number, element: HTMLDivElement) => void;
  unregisterTab: (value: string | number) => void;
};

const TabsContext = createContext<TabsContextType | null>(null);

const useTabs = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error(
      "Tabs compound components must be used within a Tabs component",
    );
  }
  return context;
};

type TabsProps = {
  defaultValue?: string;
  value?: string | number;
  onValueChange?: (value: string | number) => void;
  children: React.ReactNode;
  className?: string;
  itemsPerRow?: number | "auto";
};

type TabsComponentType = React.FC<TabsProps> & {
  Option: typeof Option;
};

const Tabs: TabsComponentType = ({
  defaultValue,
  value,
  onValueChange,
  children,
  className,
  itemsPerRow = 2,
}) => {
  const [activeInternal, setActiveInternal] = useState<string | number>(
    defaultValue || "",
  );
  const active = value !== undefined ? value : activeInternal;
  const tabRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
  const [isInitialRender, setIsInitialRender] = useState(true);

  const [activeStyles, setActiveStyles] = useState({
    width: 0,
    height: 0,
    transform: "translate(0, 0)",
  });

  const setActive = useCallback(
    (newValue: string | number) => {
      if (value === undefined) {
        setActiveInternal(newValue);
      }
      onValueChange?.(newValue);
    },
    [onValueChange, value],
  );

  const updateActiveStyles = useCallback(() => {
    const activeElement = tabRefs.current.get(active);
    if (!activeElement) return;

    const rect = activeElement.getBoundingClientRect();
    const container = activeElement.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    setActiveStyles({
      width: rect.width,
      height: rect.height,
      transform: `translate(${rect.left - containerRect.left}px, ${rect.top - containerRect.top}px)`,
    });
  }, [active]);

  const registerTab = useCallback(
    (value: string | number, element: HTMLDivElement) => {
      const refs = tabRefs.current;
      refs.set(value, element);
      updateActiveStyles();
    },
    [],
  );

  const unregisterTab = useCallback((value: string | number) => {
    tabRefs.current.delete(value);
  }, []);

  const gridTemplateStyle = useCallback(() => {
    if (itemsPerRow === "auto") {
      return {
        gridAutoFlow: "column",
        gridAutoColumns: "auto",
      };
    }

    return {
      gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))`,
    };
  }, [itemsPerRow]);

  useEffect(() => {
    updateActiveStyles();
    window.addEventListener("resize", updateActiveStyles);

    // Remove initial render flag after first render
    if (isInitialRender) {
      const timer = setTimeout(() => {
        setIsInitialRender(false);
      }, 50);
      return () => clearTimeout(timer);
    }

    return () => window.removeEventListener("resize", updateActiveStyles);
  }, [updateActiveStyles, isInitialRender]);

  return (
    <TabsContext.Provider
      value={{
        active,
        setActive,
        registerTab,
        unregisterTab,
      }}
    >
      <div className={cn("tw-rounded-md tw-bg-blue-50 tw-p-1", className)}>
        <div className="tw-relative">
          <div
            className="tw-relative tw-z-1 tw-grid tw-gap-1"
            style={gridTemplateStyle()}
          >
            {children}
          </div>
          <span
            className={cn(
              "tw-absolute tw-left-0 tw-top-0 tw-rounded tw-border tw-border-blue-100/50 tw-bg-white tw-shadow-main",
              !isInitialRender &&
                "tw-transition-all tw-duration-200 tw-ease-in-out",
            )}
            style={activeStyles}
          ></span>
        </div>
      </div>
    </TabsContext.Provider>
  );
};

const normalizedTooltip = (tooltip: string) => {
  let normalizedText = tooltip;

  normalizedText = normalizedText.replace(/\bstarter\b/gi, "Starter");
  normalizedText = normalizedText.replace(/\bpro\b/gi, "Pro");
  normalizedText = normalizedText.replace(/\bteam\b/gi, "Team");

  return normalizedText;
};

const UpgradeTooltip = ({
  children,
  tooltip,
  plan,
}: {
  children: React.ReactNode;
  tooltip: string;
  plan: string;
}) => {
  return (
    <Tooltip>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Content>
        <div className="tw-w-max tw-items-start tw-rounded-lg tw-bg-blue-base tw-px-4 tw-py-3 tw-font-sans tw-text-[11px]/tight tw-font-semibold tw-text-white">
          <span
            className="tw-mb-2 tw-block tw-text-left"
            dangerouslySetInnerHTML={{ __html: normalizedTooltip(tooltip) }}
          ></span>
          <UpgradeButton plan={plan} feature="" />
        </div>
      </Tooltip.Content>
    </Tooltip>
  );
};

type BaseOptionProps = {
  value: string | number;
  children: React.ReactNode;
  className?: string;
};

type NonProOptionProps = BaseOptionProps & {
  isLocked?: false;
  tooltip?: string;
  plan?: string;
  onLockAction?: () => void;
};

type ProOptionProps = BaseOptionProps & {
  isLocked: boolean;
  tooltip: string;
  plan: string;
  onLockAction: () => void;
};

type OptionProps = NonProOptionProps | ProOptionProps;

const Option = forwardRef<HTMLDivElement, OptionProps>(
  (
    { value, children, className, isLocked, tooltip, plan, onLockAction },
    ref,
  ) => {
    const { active, setActive, registerTab, unregisterTab } = useTabs();
    const disabled = isLocked;
    const optionRef = useRef<HTMLDivElement | null>(null);

    const setElementRef = useCallback(
      (element: HTMLDivElement | null) => {
        if (ref) {
          if (typeof ref === "function") {
            ref(element);
          } else {
            ref.current = element;
          }
        }

        optionRef.current = element;

        if (element) {
          registerTab(value, element);
        } else {
          unregisterTab(value);
        }
      },
      [ref, registerTab, unregisterTab, value],
    );

    const handleClick = () => {
      if (disabled) return onLockAction?.();
      setActive(value);
    };

    const option = (
      <span
        role="tab"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
        ref={setElementRef}
        {...(active === value && { "data-active": true })}
        className={cn(
          "tw-group tw-flex tw-h-6 tw-cursor-pointer tw-items-center tw-justify-center tw-fill-blue-500 tw-px-2 tw-text-center tw-font-sans tw-text-[10px]/normal tw-font-semibold tw-text-blue-500 tw-transition-colors tw-duration-200 tw-ease-in-out hover:tw-text-primary [&_.sd-remix]:tw-size-[18px] [&_.sd-remix]:tw-transition-colors [&_.sd-remix]:hover:tw-fill-primary [&_i]:tw-text-lg [&_i]:tw-transition-colors [&_i]:hover:tw-text-primary",
          active === value &&
            "tw-text-primary [&_.sd-remix]:tw-fill-primary [&_i]:tw-text-primary",
          disabled &&
            "tw-rounded tw-border tw-border-dashed tw-border-blue-200 hover:tw-text-blue-500",
          className,
        )}
      >
        {children}
      </span>
    );

    if (tooltip && disabled)
      return (
        <UpgradeTooltip tooltip={tooltip} plan={plan || ""}>
          {option}
        </UpgradeTooltip>
      );
    return option;
  },
);

Tabs.Option = Option;

export default Tabs;
